import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { subscriptionDiagnosticService } from '../../services/subscriptionDiagnosticService';
import { caregiverService } from '../../services/caregiverService';
import Icon from '../AppIcon';

const UserProfileHeader = () => {
  const { userProfile, user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [trialStatus, setTrialStatus] = useState({ inTrial: false, daysRemaining: 0 });
  const [profileType, setProfileType] = useState('Self');

  // Run diagnostic on mount for debugging
  useEffect(() => {
    if (user?.email === 'koibizugbe+test@gmail.com') {
      subscriptionDiagnosticService?.getDiagnosticData(user?.email);
    }
  }, [user?.email]);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!authLoading && userProfile) {
        try {
          // CRITICAL: Force fresh fetch with cache-busting timestamp
          const timestamp = Date.now();
          console.log(`[UserProfileHeader ${timestamp}] Fetching subscription for user:`, user?.email);
          
          // Force fresh fetch from database
          const { data, error } = await subscriptionService?.getCurrentSubscription();
          
          if (error) {
            console.error('[UserProfileHeader] Subscription fetch error:', error);
          } else {
            console.log('[UserProfileHeader] Fresh subscription data:', {
              planType: data?.plan?.planType,
              planName: data?.plan?.name,
              status: data?.status,
              userId: data?.userId,
              timestamp
            });
            setSubscription(data);
          }
          
          const trialInfo = await subscriptionService?.isUserInTrial();
          setTrialStatus(trialInfo);

          // Determine profile type based on managed profiles
          const { data: managedProfiles } = await caregiverService?.getManagedProfiles();
          if (managedProfiles && managedProfiles?.length > 0) {
            setProfileType('Caregiver');
          } else {
            setProfileType('Self');
          }
        } catch (error) {
          console.error('[UserProfileHeader] Failed to fetch subscription:', error);
        }
      }
    };

    fetchSubscription();
  }, [authLoading, userProfile, user?.email]);

  // CRITICAL FIX: Show loading skeleton ONLY during initial auth loading
  // Once user is authenticated, show actual data even if profile is still loading
  if (authLoading) {
    return (
      <div className="fixed top-2 right-2 z-[60] flex items-center gap-2 bg-white rounded-lg shadow-md px-2.5 py-1.5 border border-gray-200">
        {/* User Icon Skeleton */}
        <div className="w-6 h-6 rounded-full bg-gray-200 flex-shrink-0 animate-pulse" />
        
        {/* User Info Skeleton */}
        <div className="flex flex-col gap-1">
          {/* Line 1 Skeleton */}
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
            <span className="text-gray-400">|</span>
            <div className="h-3 w-12 bg-gray-200 rounded animate-pulse" />
          </div>
          
          {/* Line 2 Skeleton */}
          <div className="flex items-center gap-1.5">
            <div className="h-2.5 w-28 bg-gray-200 rounded animate-pulse" />
            <span className="text-gray-400">|</span>
            <div className="h-4 w-16 bg-gray-200 rounded-md animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated, don't show header at all
  if (!user) {
    return null;
  }

  // Check if user is owner account
  const isOwner = user?.email?.toLowerCase() === 'kingsley@mybodytoday1.com';
  
  // Use userProfile if available, otherwise fall back to user data from auth
  const userName = userProfile 
    ? `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`?.trim() || userProfile?.email?.split('@')?.[0] || 'User'
    : user?.email?.split('@')?.[0] || 'User';
  const userEmail = user?.email || userProfile?.email;
  
  // CRITICAL: Use actual subscription data from database
  // If subscription is still loading, show default 'Basic' plan instead of skeleton
  const planType = isOwner ? 'full' : (subscription?.plan?.planType || 'basic');
  const rawAccountType = isOwner ? 'Full Plan' : (subscription?.plan?.name || 'Basic');
  // Rename 'Basic' to 'Free Plan' for display
  const accountType = rawAccountType === 'Basic' || rawAccountType === 'basic' ? 'Free Plan' : rawAccountType;
  
  console.log('[UserProfileHeader] Rendering with:', { planType, accountType, email: user?.email });

  // Color coding based on plan type
  const getPlanColor = () => {
    if (isOwner) {
      return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    }
    switch (planType) {
      case 'premium':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'family':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPlanIcon = () => {
    if (isOwner) {
      return 'Shield';
    }
    switch (planType) {
      case 'premium':
        return 'Crown';
      case 'family':
        return 'Users';
      default:
        return 'User';
    }
  };

  return (
    <div className="fixed top-2 right-2 z-[60] flex items-center gap-2 bg-white rounded-lg shadow-md px-2.5 py-1.5 border border-gray-200">
      {/* User Icon */}
      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon name="User" size={12} color="var(--color-primary)" />
      </div>
      
      {/* User Info - 2 Line Format */}
      <div className="flex flex-col gap-0.5">
        {/* Line 1: Name | Profile Type */}
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-medium text-foreground">{userName}</span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-700 font-medium">{profileType}</span>
        </div>
        
        {/* Line 2: Email | Plan Badge */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-gray-600">{userEmail}</span>
          <span className="text-gray-300">|</span>
          
          {/* Account Type Badge */}
          <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border ${getPlanColor()}`}>
            <Icon name={getPlanIcon()} size={9} />
            <span className="text-[9px] font-semibold uppercase tracking-wide">
              {accountType}
            </span>
          </div>
        </div>
      </div>

      {/* Trial Countdown Badge */}
      {trialStatus?.inTrial && trialStatus?.daysRemaining <= 7 && (
        <>
          <div className="w-px h-full bg-gray-200" />
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-md border bg-orange-50 text-orange-600 border-orange-200">
            <Icon name="Clock" size={10} />
            <span className="text-[9px] font-semibold">
              {trialStatus?.daysRemaining}d left
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfileHeader;